import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../stores/useAuthStore';
import { resolvePostLoginRoute } from '../../utils/postLoginRoute';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { Card, CardContent } from '@/components/ui/card';
import iconVib3 from '@/assets/icon-vib3.svg';

type Status = 'checking' | 'ready' | 'invalid' | 'saving' | 'done';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const { setNewPassword, refreshSession, error } = useAuthStore();
  const [status, setStatus] = useState<Status>('checking');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const ran = useRef(false);

  // Materialise the recovery session from the URL hash on mount.
  // supabase-js v2 auto-parses `#access_token=...&type=recovery` when
  // getSession() is called — same pattern as AuthCallbackPage.
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    (async () => {
      const { data, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !data.session) {
        setStatus('invalid');
        return;
      }
      setStatus('ready');
    })();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (password.length < 6) {
      setLocalError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setLocalError("Passwords don't match.");
      return;
    }

    setStatus('saving');
    await setNewPassword(password);
    const state = useAuthStore.getState();
    if (state.error) {
      // updateUser failed — most commonly because the recovery session
      // expired between page load and submit.
      setStatus('ready');
      return;
    }

    // Force a fresh JWT so any custom claims (user_role) are up to date,
    // then route by workflow stage like AuthCallbackPage does.
    await refreshSession();
    const userId = useAuthStore.getState().user?.id;
    if (!userId) {
      setStatus('ready');
      setLocalError('Lost session while saving — please request a new link.');
      return;
    }
    const route = await resolvePostLoginRoute(userId, {
      isEmployee: useAuthStore.getState().userRole === 'employee',
    });
    setStatus('done');
    navigate(route, { replace: true });
  };

  if (status === 'checking') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-sm text-muted-foreground">
        Verifying reset link…
      </div>
    );
  }

  if (status === 'invalid') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Card className="w-full max-w-[400px]">
          <CardContent className="p-8 text-center">
            <img src={iconVib3} alt="Vib3" className="mx-auto mb-3 h-8 w-auto" />
            <h2 className="text-lg font-semibold text-foreground">Link is invalid or expired</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Reset links work for a single use and expire after a short time.
              Request a new one from the sign-in screen.
            </p>
            <Button
              type="button"
              className="mt-6 w-full cursor-pointer"
              onClick={() => navigate('/login', { replace: true })}
            >
              Back to sign in
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const submitDisabled = status === 'saving' || password.length === 0 || confirm.length === 0;
  const visibleError = localError ?? (error || null);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <Card className="w-full max-w-[400px]">
        <CardContent className="p-8">
          <div className="mb-6 text-center">
            <img src={iconVib3} alt="Vib3" className="mx-auto mb-3 h-8 w-auto" />
            <h2 className="text-lg font-semibold text-foreground">Set a new password</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Choose a password you'll remember.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="new-password">New password</Label>
              <PasswordInput
                id="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min 6 characters"
                required
                minLength={6}
                autoComplete="new-password"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="confirm-password">Confirm password</Label>
              <PasswordInput
                id="confirm-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Type it again"
                required
                minLength={6}
                autoComplete="new-password"
              />
            </div>

            {visibleError && (
              <p className="text-sm text-destructive">{visibleError}</p>
            )}

            <Button
              type="submit"
              disabled={submitDisabled}
              className="w-full cursor-pointer"
            >
              {status === 'saving' ? 'Saving…' : 'Save new password'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
